import { useState, useCallback } from "react";
import { quickReferService } from "../api/quickrefer/QuickReferService";
import {
  QuickRefer,
  QuickReferPayload,
} from "../api/quickrefer/types/QuickRefer.types";
import { Result } from "@badrap/result";

interface UseQuickReferState {
  referral: QuickRefer | null;
  loading: {
    getReferralById: boolean;
    getReferrals: boolean;
    saveReferral: boolean;
  };
  error: {
    getReferralById: string | null;
    getReferrals: string | null;
    saveReferral: string | null;
  };
}

interface UseQuickReferReturn extends UseQuickReferState {
  getReferralById: (
    pas: string,
    applicationId: string
  ) => Promise<QuickRefer | null>;
  getReferrals: (pas: string) => Promise<QuickRefer[]>;
  saveReferral: (
    payload: QuickReferPayload
  ) => Promise<Result<QuickRefer, Error>>;
  clearGetReferralByIdError: () => void;
  clearSaveReferralError: () => void;
  clearGetReferralsError: () => void;
  clearErrors: () => void;
}

export const useQuickRefer = (): UseQuickReferReturn => {
  const [state, setState] = useState<UseQuickReferState>({
    referral: null,
    loading: {
      getReferralById: false,
      getReferrals: false,
      saveReferral: false,
    },
    error: {
      getReferralById: null,
      getReferrals: null,
      saveReferral: null,
    },
  });

  const getReferralById = useCallback(
    async (pas: string, applicationId: string): Promise<QuickRefer | null> => {
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, getReferralById: true },
        error: { ...prev.error, getReferralById: null },
      }));

      const result = await quickReferService.getReferralById(
        pas,
        applicationId
      );

      if (result.isErr) {
        const errorMessage =
          result.error.message || "Failed to get referral status";
        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, getReferralById: false },
          error: { ...prev.error, getReferralById: errorMessage },
        }));
        return null;
      }

      if (result.value === null) {
        setState((prev) => ({
          ...prev,
          referral: null,
          loading: { ...prev.loading, getReferralById: false },
        }));
        return null;
      }

      setState((prev) => ({
        ...prev,
        referral: result.value,
        loading: { ...prev.loading, getReferralById: false },
      }));
      return result.value;
    },
    []
  );

  const getReferrals = useCallback(
    async (pas: string): Promise<QuickRefer[]> => {
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, getReferrals: true },
        error: { ...prev.error, getReferrals: null },
      }));

      const result = await quickReferService.getReferrals(pas);

      if (result.isErr) {
        const errorMessage = result.error.message || "Failed to get referrals";
        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, getReferrals: false },
          error: { ...prev.error, getReferrals: errorMessage },
        }));
        return [];
      }

      setState((prev) => ({
        ...prev,
        referrals: result.value,
        loading: { ...prev.loading, getReferrals: false },
      }));

      return result.value;
    },
    []
  );

  const saveReferral = useCallback(
    async (payload: QuickReferPayload): Promise<Result<QuickRefer, Error>> => {
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, saveReferral: true },
        error: { ...prev.error, saveReferral: null },
      }));

      const result = await quickReferService.saveReferral(payload);

      if (result.isErr) {
        const errorMessage = result.error.message || "Failed to save referral";
        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, saveReferral: false },
          error: { ...prev.error, saveReferral: errorMessage },
        }));
        return result;
      }

      setState((prev) => ({
        ...prev,
        referral: result.value,
        loading: { ...prev.loading, saveReferral: false },
      }));
      return result;
    },
    []
  );

  const clearErrors = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: { getReferralById: null, getReferrals: null, saveReferral: null },
    }));
  }, []);

  const clearGetReferralByIdError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: { ...prev.error, getReferralById: null },
    }));
  }, []);

  const clearGetReferralsError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: { ...prev.error, getReferrals: null },
    }));
  }, []);

  const clearSaveReferralError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: { ...prev.error, saveReferral: null },
    }));
  }, []);

  return {
    ...state,
    getReferralById,
    getReferrals,
    saveReferral,
    clearGetReferralByIdError,
    clearSaveReferralError,
    clearGetReferralsError,
    clearErrors,
  };
};
